import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { ProfileSkeleton } from "@/components/Skeleton";
import {
  FACILITY_DETAILS_HEADER_QUERY,
  FacilityDetailsHeaderQueryResponse
} from "@/pages/facilities/[facilityId]/queries";
import ObjectHeading from "@/components/ObjectHeading";
import { displayableFacilityLegacyId } from "@/lib/sectionLegacyId";
import { useRecentlyVisited } from "@/hooks/useRecentlyVisited";

export interface MobileFacilityOverviewProps {
  facilityId: string;
}

export default function MobileFacilityOverview({ facilityId }: MobileFacilityOverviewProps) {
  const { addRecentlyVisitedItem } = useRecentlyVisited({ skipListQuery: true });
  const { data, loading, error } = useQuery<FacilityDetailsHeaderQueryResponse>(FACILITY_DETAILS_HEADER_QUERY, {
    variables: { facilityId }
  });

  useEffect(() => {
    const facility = data?.facility;
    if (!facility) return;

    addRecentlyVisitedItem({ type: "facilities", entityId: facility.id });
  }, [addRecentlyVisitedItem, data?.facility]);

  if (loading) return <ProfileSkeleton />;
  if (error) return <div>Error loading facility details</div>;
  if (!data?.facility) return <div>Facility not found</div>;

  const { facility } = data;
  const displayableLegacyId = displayableFacilityLegacyId(facility.legacyId);

  return (
    <div
      className="bg-sys-brand-surface pb-4 border-b border-sys-brand-outline-variant"
      data-testid="mobile-facility-overview"
    >
      <ObjectHeading
        title={facility.name}
        iconName="location_on"
        variant="landing-page"
        metaData={[
          ...(facility.section?.name ? [{ text: facility.section.name, label: "Section" }] : []),
          ...(displayableLegacyId ? [{ text: displayableLegacyId, label: "Facility ID", copyable: true }] : [])
        ]}
      />
    </div>
  );
}
